import { useState, useEffect } from 'react'
import {
  FiBell,
  FiCheckCircle,
  FiAlertCircle,
  FiXCircle,
  FiInfo,
  FiLoader,
  FiTrash2,
  FiFilter,
  FiCheck,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function Notifications() {
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterType, setFilterType] = useState('all') // all, unread, success, error, warning, info
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [marking, setMarking] = useState(null)
  const limit = 10

  useEffect(() => {
    fetchNotifications()
  }, [page, filterType])

  const fetchNotifications = async () => {
    try {
      setLoading(true)
      const params = {
        page,
        limit,
      }

      if (filterType === 'unread') {
        params.unreadOnly = true
      } else if (filterType !== 'all') {
        params.type = filterType
      }

      const response = await axios.get('/notifications', { params })
      setNotifications(response.data.notifications || [])
      setTotalPages(response.data.totalPages || 1)
      setTotal(response.data.total || 0)
    } catch (error) {
      console.error('Error fetching notifications:', error)
      toast.error('Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationId) => {
    setMarking(notificationId)
    try {
      await axios.patch(`/notifications/${notificationId}/read`)
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      )
      toast.success('Marked as read')
    } catch (error) {
      console.error('Error marking as read:', error)
      toast.error('Failed to mark as read')
    } finally {
      setMarking(null)
    }
  }

  const markAllAsRead = async () => {
    try {
      await axios.patch('/notifications/mark-all-read')
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      toast.success('All notifications marked as read')
    } catch (error) {
      console.error('Error marking all as read:', error)
      toast.error('Failed to mark all as read')
    }
  }

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`/notifications/${notificationId}`)
      setNotifications(prev => prev.filter(n => n._id !== notificationId))
      setTotal(prev => prev - 1)
      toast.success('Notification deleted')
    } catch (error) {
      console.error('Error deleting notification:', error)
      toast.error('Failed to delete notification')
    }
  }

  const getNotificationIcon = (type) => {
    const iconProps = 'w-5 h-5'
    switch (type) {
      case 'success':
        return <FiCheckCircle className={`${iconProps} text-success-600`} />
      case 'error':
        return <FiXCircle className={`${iconProps} text-error-600`} />
      case 'warning':
        return <FiAlertCircle className={`${iconProps} text-warning-600`} />
      default:
        return <FiInfo className={`${iconProps} text-blue-600`} />
    }
  }

  const getNotificationBgColor = (type, isRead) => {
    if (isRead) return 'bg-white hover:bg-gray-50'
    switch (type) {
      case 'success':
        return 'bg-success-50 hover:bg-success-100'
      case 'error':
        return 'bg-error-50 hover:bg-error-100'
      case 'warning':
        return 'bg-warning-50 hover:bg-warning-100'
      default:
        return 'bg-blue-50 hover:bg-blue-100'
    }
  }

  const formatTime = (date) => {
    const now = new Date()
    const diff = now - new Date(date)
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`

    return new Date(date).toLocaleDateString()
  }

  const filterButtons = [
    { value: 'all', label: 'All', count: total },
    { value: 'unread', label: 'Unread', count: notifications.filter(n => !n.read).length },
    { value: 'success', label: 'Success' },
    { value: 'error', label: 'Error' },
    { value: 'warning', label: 'Warning' },
    { value: 'info', label: 'Info' }
  ]

  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
              <p className="text-gray-600 text-sm mt-1">Stay updated with your account activity</p>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {filterButtons.map((btn) => (
              <button
                key={btn.value}
                onClick={() => {
                  setFilterType(btn.value)
                  setPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  filterType === btn.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <FiFilter className="w-4 h-4" />
                {btn.label}
                {btn.count > 0 && (
                  <span className="bg-gray-200 text-gray-900 rounded-full px-2 py-0.5 text-xs font-semibold">
                    {btn.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <FiLoader className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : notifications.length > 0 ? (
          <div>
            {/* Notifications List */}
            <div className="space-y-3 mb-8">
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`p-4 rounded-lg border border-gray-200 transition-all duration-150 ${
                    getNotificationBgColor(notification.type, notification.read)
                  }`}
                >
                  <div className="flex gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">
                            {notification.title}
                            {!notification.read && (
                              <span className="ml-2 inline-block w-2 h-2 bg-primary-600 rounded-full"></span>
                            )}
                          </h3>
                          <p className="text-gray-700 text-sm mt-1">
                            {notification.message}
                          </p>
                          <p className="text-gray-500 text-xs mt-2">
                            {formatTime(notification.createdAt)}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 flex-shrink-0">
                          {!notification.read && (
                            <button
                              onClick={() => markAsRead(notification._id)}
                              disabled={marking === notification._id}
                              className="p-2 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                              title="Mark as read"
                            >
                              {marking === notification._id ? (
                                <FiLoader className="w-4 h-4 animate-spin text-gray-600" />
                              ) : (
                                <FiCheck className="w-4 h-4 text-gray-600 hover:text-gray-900" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notification._id)}
                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                            title="Delete notification"
                          >
                            <FiTrash2 className="w-4 h-4 text-gray-600 hover:text-error-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        page === p
                          ? 'bg-primary-600 text-white'
                          : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <FiBell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No notifications</h3>
            <p className="text-gray-600">You're all caught up! Check back later for updates.</p>
          </div>
        )}
      </div>
    </div>
  )
}
